import type { Polygon, Position } from "geojson";
import * as turf from "@turf/turf";
import { fx_truncarPolygon } from "./AoiPolygonUnion";

/** Área en hectáreas, formateada a 2 decimales — compartida por
 *  `LoteSplitMapRender`/`Paso4InstructionsRender` (misma cuenta en los 2
 *  lugares que muestran el área de una pieza). */
export function loteAreaHectareas(polygon: Polygon): string {
  try {
    return (turf.area(polygon) / 10000).toFixed(2);
  } catch {
    return "0.00";
  }
}

/** `[minLng, minLat, maxLng, maxLat]` de un polígono (todos sus anillos), o
 *  `null` si no tiene coordenadas. Es la cuenta de `LoteSplitState.bboxDe`,
 *  suelta para poder medir también un lote que ya salió del estado (uno
 *  borrado, o el que se cortó): la usa el repintado por zona del paso 4
 *  (2026-10-27, ver `RegistroFincaPaso4Render._zonaQueCambio`). */
export function fx_cajaDePoligono(
  polygon: Polygon,
): [number, number, number, number] | null {
  let minLng = Infinity, minLat = Infinity;
  let maxLng = -Infinity, maxLat = -Infinity;
  for (const anillo of polygon.coordinates) {
    for (const [lng, lat] of anillo) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }
  return Number.isFinite(minLng) ? [minLng, minLat, maxLng, maxLat] : null;
}

/** La caja que abarca a las dos; si una es `null`, la otra. */
export function fx_unirCajas(
  a: [number, number, number, number] | null,
  b: [number, number, number, number] | null,
): [number, number, number, number] | null {
  if (!a) return b;
  if (!b) return a;
  return [
    Math.min(a[0], b[0]),
    Math.min(a[1], b[1]),
    Math.max(a[2], b[2]),
    Math.max(a[3], b[3]),
  ];
}

/** Mismo set de 15 colores que el control original — se repite en ciclo si
 *  hay más piezas. Compartido por `LoteSplitMapRender`/`Paso4InstructionsRender`
 *  (mismo orden en los 2 — la pieza `i` siempre tiene el mismo color en el
 *  mapa y la tabla de indicaciones). */
export const LOTE_COLORS = [
  "#e6194b",
  "#3cb44b",
  "#4363d8",
  "#f58231",
  "#911eb4",
  "#42d4f4",
  "#f032e6",
  "#bfef45",
  "#fabed4",
  "#469990",
  "#dcbeff",
  "#9A6324",
  "#fffac8",
  "#800000",
  "#aaffc3",
];

/** Una pieza del AOI en el paso 4 (futuro lote). `id` es una identidad local
 *  incremental (no persiste, solo sirve para trackear la pieza en el mapa y
 *  la lista mientras dura el paso — resalte por foco, `data-lote-id`) — el
 *  server asigna sus propios ids recién en el guardado final del wizard
 *  (todavía sin construir). `isCustomName` (histórico): hoy ningún nombre se
 *  recalcula; un lote sin nombre recibe el siguiente "Lote N" libre una sola
 *  vez (ver `_nombrarSinNombre`).
 *  `loteOrigenId` es un GUID de correlación (solo en memoria, dentro del
 *  borrador — ver `RegistroFincaBorrador`): comparten el mismo valor las
 *  dos mitades de un mismo corte, para poder ofrecer "Fusionar" mientras
 *  dure la sesión del wizard (no sobrevive cerrar el modal, el borrador es
 *  puramente en memoria) — `null` si la pieza no viene de un corte (o ya se
 *  fusionó de vuelta). */
export type LoteSplitEntry = {
  id: number;
  polygon: Polygon;
  nombreLote: string;
  isCustomName: boolean;
  loteOrigenId: string | null;
  /** Id del lote en el registro temporal del servidor; `null` mientras su
   *  alta todavía no se confirmó (la asigna `LotesTempSync`). */
  tempId: string | null;
  /** Asignación del paso 5 que el lote ya traía. Solo la conservan los
   *  lotes que no se tocan: una pieza nueva (corte o unión) nace sin
   *  asignar. */
  asignacion: AsignacionLoteSplit | null;
};

/** Asignación del paso 5 de un lote (área productiva + cultivo/cobertura). */
export type AsignacionLoteSplit = {
  isAreaProductiva: boolean | null;
  cultivoId: string | null;
  coberturaId: string | null;
};

/** Cambio de estructura de los lotes (corte o unión), para que quien
 *  persiste (`LotesTempSync`) lo refleje en el servidor: `eliminados` salen,
 *  `agregados` entran, y `vecinos` son lotes que siguen pero cuya geometría
 *  cambió (vértice insertado por `_sellarBordes`). Se emite ANTES del
 *  aviso general de cambio. */
export type CambioEstructuralLotes = {
  eliminados: LoteSplitEntry[];
  agregados: LoteSplitEntry[];
  vecinos: LoteSplitEntry[];
};

/** Semilla para poblar el estado (`init`) — un polígono más, opcionalmente,
 *  el `loteOrigenId` que ya traía guardado (al reabrir el paso 4 desde
 *  lotes ya persistidos). Los polígonos recién dibujados en el paso 3 no
 *  tienen `loteOrigenId` todavía (nunca pasaron por un corte). */
export type LoteSplitSeed = {
  polygon: Polygon;
  loteOrigenId?: string | null;
  /** Lote que ya existe en el registro temporal: su id, nombre y
   *  asignación guardados. */
  tempId?: string | null;
  nombreLote?: string;
  asignacion?: AsignacionLoteSplit | null;
};

/** Nombre por defecto de un lote: "Lote N". El siguiente libre es el mayor
 *  N en uso + 1 (ver `_nombrarSinNombre`). */
const NOMBRE_DEFAULT = /^Lote (\d+)$/i;

/** Estado compartido del paso 4 (mapa + panel de indicaciones lo leen/
 *  escriben por acá, en vez de coordinarse directo entre sí). Arranca con
 *  **una pieza por polígono inicial** (normalmente uno por cada polígono
 *  que el usuario dibujó en el paso 3, ver `RegistroFincaPaso4Render`) —
 *  dividir más es opcional, el backend acepta guardar así. Los nombres son
 *  estables: un lote conserva el suyo mientras exista, y una pieza nueva
 *  (corte o unión) recibe el siguiente "Lote N" libre — el mayor N en uso
 *  + 1, nunca un número repetido (`_nombrarSinNombre`). Antes se renumeraba
 *  todo por posición en cada cambio (como el control original), lo que
 *  cambiaba nombres ya guardados y podía dejar dos lotes con el mismo
 *  nombre. Vaciar el campo no deja la pieza sin nombre: conserva el que
 *  tenía (ver `rename`). */
/** Distancia por debajo de la cual dos vértices consecutivos se toman como el
 *  MISMO, en grados. ~1e-9 grados es del orden de 0.1 mm. Hace falta porque
 *  `turf.cleanCoords` solo borra duplicados EXACTOS, y las piezas que salen
 *  de cortes y uniones anteriores arrastran pares que difieren en el
 *  decimoquinto decimal. */
const TOLERANCIA_DUPLICADO_GRADOS = 1e-9;

/* `AREA_SOLAPE_MINIMA_M2` se eliminó junto con `agregarLote`.

   Era el umbral con el que este estado decidía si un lote dibujado a mano pisaba
   a otro: la última validación geométrica que corría en el navegador. El lote
   dibujado ahora lo arma y lo valida el servidor (`DibujarLote`), igual que el
   corte, la unión y los vértices, así que en el cliente no queda ninguna.

   El umbral sigue existiendo, con el mismo valor y el mismo motivo —compartir un
   borde da una intersección de área despreciable y es lo normal entre lotes—,
   pero del lado del servidor (`ToleranciaAreaMetrosCuadrados`). */

export class LoteSplitState {
  private _entries: LoteSplitEntry[] = [];
  private _nextId = 1;
  /** Varios oyentes (no uno solo): mapa y panel de indicaciones escuchan el
   *  mismo estado de forma independiente. */
  private _listeners: (() => void)[] = [];
  private _listenersEstructura: ((cambio: CambioEstructuralLotes) => void)[] = [];

  addOnChangeListener(handler: () => void): void {
    this._listeners.push(handler);
  }

  /** Corte o unión: qué lotes salen, cuáles entran y cuáles cambiaron de
   *  geometría (ver `CambioEstructuralLotes`). */
  addOnCambioEstructuralListener(
    handler: (cambio: CambioEstructuralLotes) => void,
  ): void {
    this._listenersEstructura.push(handler);
  }

  private _notifyCambioEstructural(cambio: CambioEstructuralLotes): void {
    this._listenersEstructura.forEach((handler) => handler(cambio));
  }

  private _notifyChange(): void {
    this._listeners.forEach((handler) => handler());
  }

  /** Carga los lotes tal como están en la base: geometría y nombre sin
   *  tocar. Abrir el paso 4 no cambia nada — lo que se guarda lo deciden
   *  "Dividir" y "Unir" (y los nombres que escribe el usuario). */
  init(seeds: LoteSplitSeed[]): void {
    this._entries = seeds.map((seed) => {
      const entry = this._createEntry(seed.polygon, seed.loteOrigenId ?? null);
      entry.tempId = seed.tempId ?? null;
      entry.asignacion = seed.asignacion ?? null;
      if (seed.nombreLote) {
        entry.nombreLote = seed.nombreLote;
        entry.isCustomName = true;
      }
      return entry;
    });
    this._nombrarSinNombre();
    this._notifyChange();
  }

  getAll(): readonly LoteSplitEntry[] {
    return this._entries;
  }

  /** El lote que está bajo un punto, o `null`.
   *
   *  **Es el hit-testing del click**, y existe porque los lotes dejaron de ser
   *  capas de Leaflet: sin capas no hay `layer.on("click")` que diga qué lote se
   *  tocó, así que se pregunta al modelo. Lo mismo que hace el paso 3
   *  (`AoiPiezasState.enElPunto`), y por lo mismo.
   *
   *  El borde cuenta como adentro (sin `ignoreBoundary`): un click justo sobre
   *  el contorno tiene que elegir el lote. Cuando el punto cae en un borde
   *  COMPARTIDO gana el primero del arreglo, y da igual cuál: a esa distancia
   *  los dos son el lote que el usuario quiso tocar.
   *
   *  Se descarta por caja antes de tocar turf, igual que en el paso 3: esto
   *  corre también en cada `mousemove` del modo Editar. */
  enElPunto(lng: number, lat: number): number | null {
    const punto = turf.point([lng, lat]);
    for (const entry of this._entries) {
      const caja = this.bboxDe(entry.id);
      if (!caja) continue;
      if (lng < caja[0] || lng > caja[2] || lat < caja[1] || lat > caja[3]) {
        continue;
      }
      try {
        if (turf.booleanPointInPolygon(punto, entry.polygon)) return entry.id;
      } catch {
        // Geometría inválida: no puede capturar el click.
      }
    }
    return null;
  }

  /** `[minLng, minLat, maxLng, maxLat]` de un lote, para acotar al área que
   *  cambió los tiles que se le piden al WMS. */
  bboxDe(id: number): [number, number, number, number] | null {
    const entry = this._entries.find((e) => e.id === id);
    return entry ? fx_cajaDePoligono(entry.polygon) : null;
  }

  /** La caja que abarca TODOS los lotes, para encuadrar el mapa y para decirle
   *  al WMS dónde están los datos. `null` sin lotes. */
  bboxTotal(): [number, number, number, number] | null {
    let total: [number, number, number, number] | null = null;
    for (const entry of this._entries) {
      const caja = this.bboxDe(entry.id);
      if (!caja) continue;
      total = total
        ? [
            Math.min(total[0], caja[0]),
            Math.min(total[1], caja[1]),
            Math.max(total[2], caja[2]),
            Math.max(total[3], caja[3]),
          ]
        : caja;
    }
    return total;
  }

  /** El id del lote en el registro temporal del servidor, que es con el que se
   *  filtran las capas WMS. `null` mientras su alta no volvió. */
  tempIdDe(id: number): string | null {
    return this._entries.find((e) => e.id === id)?.tempId ?? null;
  }

  /** Colapsa los vértices consecutivos que están prácticamente en el mismo
   *  punto. `turf.cleanCoords` solo borra los duplicados EXACTOS; las piezas
   *  que vienen de cortes y uniones previas traen pares que difieren en el
   *  decimoquinto decimal y sobreviven a esa pasada, ensuciando el anillo y
   *  confundiendo a la detección de pinchos. */
  private _quitarCasiDuplicados(anilloCerrado: Position[]): Position[] {
    if (anilloCerrado.length < 5) return anilloCerrado;

    const puntos = anilloCerrado.slice(0, -1);
    const unicos: Position[] = [];
    for (const punto of puntos) {
      const previo = unicos[unicos.length - 1];
      if (previo && this._mismoPunto(previo, punto)) continue;
      unicos.push(punto);
    }
    // El último puede coincidir con el primero al cerrar la vuelta.
    while (
      unicos.length > 3 &&
      this._mismoPunto(unicos[unicos.length - 1], unicos[0])
    ) {
      unicos.pop();
    }

    if (unicos.length < 3) return anilloCerrado;
    return [...unicos, unicos[0]];
  }

  private _mismoPunto(a: Position, b: Position): boolean {
    return (
      Math.abs(a[0] - b[0]) < TOLERANCIA_DUPLICADO_GRADOS &&
      Math.abs(a[1] - b[1]) < TOLERANCIA_DUPLICADO_GRADOS
    );
  }

  /** Borra un lote entero. `false` (sin tocar nada) si no existe o si es el
   *  ÚLTIMO que queda: un registro sin lotes no se puede finalizar (el paso 6
   *  lo rechaza con `REGISTRO_INCOMPLETO`) y además dejaría al paso 4 sin nada
   *  que mostrar. Para sacarlo hay que descartar el registro, no vaciarlo.
   *
   *  El polígono padre se achica solo: se recalcula desde los lotes en cada
   *  cambio estructural (ver `RegistroFincaPaso4Render`). */
  eliminarLote(id: number): boolean {
    if (this._entries.length <= 1) return false;
    const entry = this._entries.find((e) => e.id === id);
    if (!entry) return false;

    this._entries = this._entries.filter((e) => e !== entry);
    this._notifyCambioEstructural({
      eliminados: [entry],
      agregados: [],
      vecinos: [],
    });
    this._notifyChange();
    return true;
  }

  rename(id: number, nombreLote: string): void {
    const entry = this._entries.find((e) => e.id === id);
    if (!entry) return;

    // Vacío = se deja el nombre que tenía: todo lote necesita uno, y darle
    // otro número por su cuenta confundiría más que conservarlo.
    const trimmed = nombreLote.trim();
    if (trimmed.length > 0) {
      entry.isCustomName = true;
      entry.nombreLote = trimmed;
    }
    this._notifyChange();
  }

  /** Punto ÚNICO por el que pasa toda pieza que entra al estado: las
   *  semillas del paso 3, las dos mitades de cada corte y el resultado de
   *  cada unión. Por eso el redondeo va acá y no repartido en cada llamador
   *  — nada puede entrar con otra precisión. Ver `PRECISION_COORDENADAS`. */
  private _createEntry(
    polygon: Polygon,
    loteOrigenId: string | null = null,
  ): LoteSplitEntry {
    const id = this._nextId++;
    return {
      id,
      polygon: this._normalizar(polygon),
      nombreLote: "",
      isCustomName: false,
      loteOrigenId,
      tempId: null,
      asignacion: null,
    };
  }

  /** Redondea a 7 decimales y ADEMAS colapsa los vertices consecutivos
   *  coincidentes, anillo por anillo.
   *
   *  Lo segundo faltaba: `_quitarCasiDuplicados` solo corria en la union
   *  (`_limpiarTopologia`), nunca en el camino del corte. Las piezas que
   *  salian de un split arrastraban vertices duplicados EXACTOS —caso real
   *  medido: un anillo con `[-75.661754,4.618048]` repetido— que son aristas
   *  de longitud cero. `segSegIntersect` las descarta por `denom` nulo, pero
   *  al reconstruir los anillos del corte siguiente esos puntos degenerados
   *  ensucian el resultado, y como cada corte hereda la basura del anterior
   *  el problema se agrava a medida que se divide.
   *
   *  Va en `_createEntry` por lo mismo que el redondeo: es el punto UNICO por
   *  el que pasa toda pieza que entra al estado. */
  private _normalizar(polygon: Polygon): Polygon {
    const truncado = fx_truncarPolygon(polygon);
    return {
      ...truncado,
      coordinates: truncado.coordinates.map((anillo) =>
        this._quitarCasiDuplicados(anillo),
      ),
    };
  }

  /** Nombra los lotes que todavía no tienen nombre (las piezas recién
   *  salidas de un corte o de una unión) con el siguiente "Lote N" libre: el
   *  mayor N en uso + 1, en el orden de la lista. Un nombre ya asignado NO se
   *  vuelve a tocar: antes se renumeraban todos por posición en cada cambio,
   *  lo que cambiaba nombres que ya estaban guardados y podía dejar dos
   *  "Lote 11" (uno puesto por el servidor y otro por la posición). */
  private _nombrarSinNombre(): void {
    const usados = new Set(this._entries.map((e) => e.nombreLote.trim().toUpperCase()));
    let siguiente =
      Math.max(
        0,
        ...this._entries.map((e) => Number(NOMBRE_DEFAULT.exec(e.nombreLote.trim())?.[1] ?? 0)),
      ) + 1;
    this._entries.forEach((entry) => {
      if (entry.nombreLote.trim().length > 0) return;
      let nombre = `Lote ${siguiente++}`;
      while (usados.has(nombre.toUpperCase())) nombre = `Lote ${siguiente++}`;
      entry.nombreLote = nombre;
      usados.add(nombre.toUpperCase());
    });
  }

}
